import { Component, Inject, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AccountService } from '@obo-account/account/account.service';
import { PlanningService } from '@obo-main/services/plannings/planning.service';
import { Planning } from '@obo-main/services/plannings/plannings.models';
import { Building, Project } from '@obo-dashboard/models/projectManagement.models';
import { BuildingService } from '@obo-dashboard/services/building.service';
import { forkJoin, of } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { ProjectService } from '@obo-dashboard/services/project.service';
import { RegionService } from '@obo-main/services/region/region.service';
import { ActiveModal } from '@obo-main/services/modal/activeModal.model';
import { Logger } from '@obo-main/utils/logger/logger.service';
import { SpinnerService } from '@obo-main/services/common/spinner/spinner.service';

@Component({
    selector: 'app-project-building-selector-modal',
    template: `
        @if (showModal) {
        <form #form="ngForm">
            <div class="modal">
                <div class="modal-header">
                    <h2>
                        {{ 'PROJECTBUILDINGSELECTOR_TITLE' | translate }}
                    </h2>
                    <button class="btn-close" (click)="activeModal.dismiss()"></button>
                </div>
                <div class="modal-body">
                    <p>
                        {{ 'PROJECTBUILDINGSELECTOR_DESCRIPTION' | translate }}
                    </p>
                    <div class="mb-3 required">
                        <label class="form-label" for="projectName">{{ 'PROJECT_NAME' | translate }}</label>
                        <input
                            class="form-control form-control-border"
                            type="text"
                            name="projectName"
                            [(ngModel)]="project.name"
                            required
                            shdValidateNaming
                            #projectName="ngModel" />
                        <shd-error-field [control]="projectName.control"></shd-error-field>
                    </div>
                    <div class="mb-3 required">
                        <label class="form-label" for="buildingName">{{ 'BUILDING_NAME' | translate }}</label>
                        <input
                            class="form-control form-control-border"
                            type="text"
                            name="buildingName"
                            [(ngModel)]="building.name"
                            required
                            shdValidateNaming
                            #buildingName="ngModel" />
                        <shd-error-field [control]="buildingName.control"></shd-error-field>
                    </div>
                    <small class="form-text text-muted  ">
                        {{ 'SHARED_REQUIRED_FIELD_INFO' | translate }}
                    </small>
                </div>
                <div class="modal-footer">
                    <button class="btn btn-secondary me-2" (click)="activeModal.dismiss()">
                        {{ 'BTN_ABORT' | translate }}
                    </button>
                    <button class="btn btn-success mh-100" (click)="save()" [disabled]="form.invalid">
                        {{ 'BTN_SAVE' | translate }}
                    </button>
                </div>
            </div>
        </form>
        }
    `,
    standalone: false
})
export class ProjectBuildingSelectorModalComponent implements OnInit {
    public project: Project = new Project();
    public building: Building = new Building();
    public showModal: boolean = false;

    constructor(
        private projectService: ProjectService,
        private buildingService: BuildingService,
        private planningService: PlanningService,
        @Inject('moduleId') private moduleId: number,
        private accountService: AccountService,
        private translateService: TranslateService,
        @Inject('createPlanning') private createPlanning: boolean,
        @Inject('projectId') protected projectId: number,
        @Inject('buildingId') protected buildingId: number,
        private regionService: RegionService,
        @Inject(ActiveModal) public activeModal: ActiveModal,
        private logger: Logger,
        private spinnerService: SpinnerService
    ) {}

    public ngOnInit(): void {
        if (this.projectId && this.buildingId) {
            this.spinnerService.startSpinner();
            forkJoin([
                this.projectService.getProject(this.projectId),
                this.buildingService.getBuilding(this.projectId, this.buildingId)
            ]).subscribe({
                next: ([project, building]) => {
                    this.project = project;
                    this.building = building;
                    this.save();
                },
                error: (err) => {
                    this.logger.error('Error loading project or building', err);
                    this.initProjectAndBuilding();
                    this.showModal = true;
                },
                complete: () => {
                    this.spinnerService.stopSpinner();
                }
            });
        } else {
            this.initProjectAndBuilding();
            this.showModal = true;
        }
    }

    private initProjectAndBuilding(): void {
        this.project.name = this.translateService.instant('PROJECT_NEW');
        this.building.name = this.translateService.instant('BUILDING_NEW');
        this.building.regionId = this.regionService.selectedApplicationRegion?.id ?? this.regionService.selectedPortalRegion?.id;
    }

    public save(): void {
        this.accountService
            .getAccount()
            .pipe(
                mergeMap(() => {
                    return this.projectService.createOrUpdateProject(this.project).pipe(
                        tap((project) => {
                            this.projectService.updateProjectsUsageDateTimestamp(project.id).subscribe();
                        }),
                        mergeMap((project) => {
                            return this.buildingService.createOrUpdateBuilding(project.id, this.building).pipe(
                                mergeMap((building) => {
                                    if (this.createPlanning) {
                                        return this.planningService.createPlanning(project.id, building.id, this.moduleId).pipe(
                                            map((planning) => ({
                                                project,
                                                building,
                                                planning
                                            }))
                                        );
                                    } else {
                                        return of(new Planning()).pipe(
                                            map((planning) => ({
                                                project,
                                                building,
                                                planning
                                            }))
                                        );
                                    }
                                })
                            );
                        })
                    );
                })
            )
            .subscribe(({ project, building, planning }) => {
                this.activeModal.close({
                    projectId: project.id,
                    buildingId: building.id,
                    planningId: planning.id
                } as ProjectBuildingSelectorModalUrlParameterType);
            });
    }
}

export type ProjectBuildingSelectorModalUrlParameterType = {
    projectId: number;
    buildingId: number;
    planningId: number;
};
